import React from "react";
import { useAuth } from "@via/hooks/useAuth";
import { GenericError } from "@via/components/error.tsx";
import { Spinner, SkeletonLoader } from "@via/components/loading/loading.tsx";
import { ERROR } from "@via/utils/constants";
import {
  useSubscribe,
  useLicenseProductInfo,
  useCheckSubscriptionHealth,
} from "@via/pages/billing/billing.api.tsx";

import { formatCurrency } from "@via/utils/helpers.tsx";
import { LicenseProductInfoResponseBody, OnboardingStep } from "common";

interface ConfirmSubscriptionProps {
  paymentMethodId: string;
  onNextStep: () => void;
  onPreviousStep: () => void;
}

const ConfirmSubscription: React.FC<ConfirmSubscriptionProps> = ({
  paymentMethodId,
  onNextStep,
  onPreviousStep,
}) => {
  const { user } = useAuth();
  const { mutateAsync: subscribe, isPending } = useSubscribe();
  const {
    data: productInfo,
    isLoading: isProductInfoLoading,
    isError: isProductInfoError,
  } = useLicenseProductInfo(paymentMethodId);

  const { data: checkSubscriptionHealth } = useCheckSubscriptionHealth({
    enabled: user?.onboardingStep === OnboardingStep.OrganizationCreated,
  });

  const trialUsed =
    user?.onboardingStep === OnboardingStep.OrganizationCreated &&
    checkSubscriptionHealth?.trialUsed;

  const handleConfirm = async () => {
    try {
      await subscribe({ paymentMethodId });
      onNextStep();
    } catch {
      // handled by notifications
    }
  };

  const handleBackClick = () => {
    onPreviousStep();
  };

  const address: LicenseProductInfoResponseBody["customer"]["billing"]["address"] =
    productInfo?.customer?.billing?.address ?? {
      line1: null,
      line2: null,
      postal_code: null,
      city: null,
      state: null,
      country: null,
    };

  const fullAddress = [
    address.line1,
    address.line2,
    address.postal_code,
    address.city,
    address.state,
    address.country,
  ]
    .filter(part => part != null && part !== "")
    .join(", ");

  // Handle loading
  if (isProductInfoLoading) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="w-94 grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-1">
          <SkeletonLoader count={2} />
        </div>
      </div>
    );
  }

  // Handle error
  if (isProductInfoError || !productInfo) {
    return (
      <div className="via-generic-error lg:py-12">
        <GenericError message={ERROR} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto space-y-6">
      <h2 className="text-2xl text-center font-bold text-gray-800">
        {`Review Your ${trialUsed ? "Purchase" : "Subscription"}`}
      </h2>

      <div className="flex gap-4">
        <div className="w-1/2">
          <label className="text-sm font-medium text-gray-700">Name</label>
          <input
            value={
              productInfo?.customer?.billing?.name ||
              productInfo?.customer?.customerName ||
              ""
            }
            disabled
            className="w-full mt-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
          />
        </div>

        <div className="w-1/2">
          <label className="text-sm font-medium text-gray-700">
            Credit Card
          </label>
          <input
            value={
              productInfo.customer?.last4
                ? `**** **** **** ${productInfo.customer.last4}`
                : "**** **** ****"
            }
            disabled
            className="w-full mt-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
          />
        </div>
      </div>

      <div className="w-full">
        <label className="text-sm font-medium text-gray-700">
          Billing Address
        </label>
        <textarea
          value={fullAddress}
          disabled
          rows={2}
          className="w-full mt-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
        />
      </div>

      {productInfo?.customer?.billing?.phone && (
        <div className="w-full">
          <label className="text-sm font-medium text-gray-700">Phone</label>
          <input
            value={productInfo.customer.billing.phone}
            disabled
            className="w-full mt-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
          />
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-700 border border-gray-200">
        <div className="font-semibold text-center text-gray-900 mb-2">
          {productInfo.product.name}
        </div>
        {productInfo.product.description}
      </div>

      <div className="flex gap-4">
        {productInfo.product?.marketing_features?.map((featureObj, index) => {
          const featureText = Object.values(featureObj)?.[0] ?? "";
          const [firstWord, ...restWords] = featureText.split(" ");

          return (
            <div
              key={index}
              className="flex-1 border rounded-md p-4 bg-gray-50 flex flex-col items-center">
              <h2 className="text-2xl font-bold text-gray-800 text-center">
                {firstWord}
              </h2>
              <span className="text-sm text-gray-700 text-center">
                {restWords.join(" ")}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end items-center mt-4 font-bold text-xl text-indigo-600">
        <span className="text-xs text-gray-400 font-normal mr-4">
          Total amount
        </span>
        {formatCurrency(productInfo.price.unit_amount)} /{" "}
        {productInfo.price.recurring.interval}
      </div>

      {!trialUsed && (
        <div className="flex-1 text-center text-sm border rounded-md p-2 bg-gray-50 flex flex-col items-center">
          No payment today—your subscription fee will only apply after the
          14-day trial period
        </div>
      )}

      <div className="flex justify-center gap-4 py-4">
        <button
          onClick={handleBackClick}
          className="px-6 py-2 bg-white text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-100">
          Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={isPending}
          className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500 disabled:opacity-50">
          <Spinner
            loading={
              isPending
            }>{`Confirm ${trialUsed ? "Purchase" : "Subscription"}`}</Spinner>
        </button>
      </div>
    </div>
  );
};

export default ConfirmSubscription;
